import React, { useRef, useEffect, useState, useCallback } from 'react';
import {
  DndInput, Text,
} from '@myinterview/component-library';
import { useTranslation } from 'react-i18next';
import { uploadFile } from '../../utils/fileUtils';
import { registerCountEvent } from '../../services/sessionService';
import { emitTrackEvent, EVENT_TYPES } from '../../services/eventService';
import { UPLOAD_MIME_TYPES } from '../../views/CompanyWelcomePage/consts';
import { IInputFields } from '../../interfaces';

export const MAX_UPLOAD_FILE_SIZE_BYTE = 10 * 1024 * 1024;

const acceptedMimeTypes = UPLOAD_MIME_TYPES.join();

interface IProps {
  field: IInputFields,
  initialFileName: string,
  onUploadDoc: (data: { value: string | '', type: string, name: string }) => void;
  rtl?: boolean;
  browseText?: string;
  uploadingText?: string;
  dropHereText?: string;
}

export const UploadInput = ({
  field, onUploadDoc, initialFileName = '', rtl, browseText, uploadingText, dropHereText,
}: IProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const { t } = useTranslation();
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [fileName, setFileName] = useState(initialFileName);

  useEffect(() => { if (initialFileName) setFileName(initialFileName); }, [initialFileName]);

  const setErrorAndReset = (err: string) => {
    setError(err);
    setProgress(null);
    setFileName('');
  };

  const setValues = (name?: string, url?: string) => {
    setProgress(null);
    setFileName(name || '');
    registerCountEvent('files_uploaded_count').then(() => { });
    onUploadDoc({ value: url || '', type: field.type, name: field.fieldName });
  };

  const handleInputRef = useCallback((node: HTMLInputElement) => {
    if (node) {
      inputRef.current = node;
      inputRef.current.accept = acceptedMimeTypes;
    }
  }, []);

  const handleFileAdded = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    if (!event.target.files || !event.target.files.length) return;
    const fileSize = event.target.files[0].size;

    if (fileSize > MAX_UPLOAD_FILE_SIZE_BYTE) {
      setError(t('error.validation.fileSize'));
      return;
    }

    const { fieldName } = field;

    await uploadFile({
      event,
      fieldName,
      onSuccess: (name: string, url: string) => {
        setValues(name, url);
        emitTrackEvent({
          eventType: EVENT_TYPES.DOCUMENT_UPLOADED,
          extraData: { fieldName, name, url, fileSize },
        });
      },
      onProgress: (progressEvent) => {
        setError('');
        setProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
      },
      onError: () => {
        if (inputRef.current) inputRef.current.value = '';
        setErrorAndReset(t('error.upload_file_failed'));
        onUploadDoc({ value: '', type: field.type, name: field.fieldName });
      },
      onDone: () => { },
    });
  };

  const handleRemoveClicked = () => {
    setValues();
  };

  return (
    <>
      <Text className="dynamic-direction" size="S-Regular">
        {
          t(`input_fields.${field.fieldName}`, field.label)
        }
      </Text>
      <DndInput
        browseText={browseText}
        uploadingText={uploadingText}
        dropHereText={dropHereText}
        rtl={rtl}
        ref={handleInputRef}
        fileName={fileName && decodeURIComponent(fileName)}
        uploadPercentage={progress}
        onRemoveFile={handleRemoveClicked}
        onInput={handleFileAdded}
      />
      <Text className="dynamic-direction" size="S-Regular" color="error">{error}</Text>
    </>
  );
};
